import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { AnyAri } from '@atlassian/ari/any-ari';
import { type SearchResultType } from '@atlassian/search-client/common/types';
import { ResourceType } from '@atlassian/search-client/third-party-graph-entity';

import { ProductKeys } from '../../../../../../../../common/constants/product-keys';
import type { SearchGraphResultProps } from '../../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../../common/ui/restricted-icon';
import {
	formatKey,
	getAssigneeOrCreator,
	getStatusAndDate,
} from '../../../../../../../../common/ui/search-graph-jira-dc';
import SearchMetadataLink from '../../../../../../../../common/ui/search-metadata-link';
import { SearchResult } from '../../../../../../../../common/ui/search-result';
import { getJiraSearchResultTypeMetadata } from '../../../../../../../../common/utils/get-jira-search-result-type-metadata';
import { isNonPublic } from '../../../../../../../../common/utils/is-non-public';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../../common/utils/search-result-utils';
import type { searchGraphJiraDcProject_AtlassianSearchPage$key } from './__generated__/searchGraphJiraDcProject_AtlassianSearchPage.graphql';
import { messages } from './messages';

export const SearchGraphJiraDcProject = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	thirdPartyConfigs,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	mainContentWidth,
	selectedSites,
	productConfigs,
}: SearchGraphResultProps<searchGraphJiraDcProject_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphJiraDcProject_AtlassianSearchPage on SearchResultGraphDocument {
				id
				integrationId
				lastModifiedDate
				url
				excerpt
				title
				subtype
				permissionLevel
				entity {
					... on ExternalProject {
						assignee {
							thirdPartyUser {
								id
								name
							}
						}
						createdBy {
							thirdPartyUser {
								id
								name
							}
						}
						dueDate
						key
						status
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalSpace {
								displayName
								url
							}
						}
					}
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();
	const { lastModifiedDate, url, permissionLevel, entity, excerpt, title } = data;
	const { assignee, createdBy, dueDate, status, key } = entity || {};

	const integrationId = data.integrationId;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const entityToLabelMap: Record<string, string> = {
		space: 'space',
		'work-item': 'issue',
		project: 'issue',
		comment: 'comment',
	};

	const { resourceType } = useMemo(() => AnyAri.parse(data.id), [data.id]);

	const subtype = data.subtype
		? getJiraSearchResultTypeMetadata(data.subtype as SearchResultType)
		: null;
	const resultType = subtype || (entityToLabelMap[resourceType] ?? resourceType);
	const labelMessage = messages[resultType];
	const label = resultType && labelMessage ? formatMessage(labelMessage) : getLabel(resultType);

	// Find the specific datasource for this result based on its integrationId
	const resultDatasource =
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.datasources?.find(
			(ds) => ds.integrationId === integrationId,
		) || null;
	const site =
		resultDatasource?.workspaceName ||
		thirdPartyConfigs?.[ProductKeys.JiraDC]?.workspaceName ||
		null;

	const containerName = entity?.container?.displayName;
	const containerUrl = entity?.container?.url;
	const container =
		containerUrl && containerName ? (
			<SearchMetadataLink label={containerName} href={containerUrl} />
		) : (
			containerName
		);

	const headerMetadataParts = [
		label,
		['site', site],
		['container', container],
		['last-modified', lastModifiedComponent],
		['padlock', padlockIcon],
		['status', getStatusAndDate(status, dueDate)],
		[
			'person',
			getAssigneeOrCreator(
				formatMessage,
				assignee?.thirdPartyUser?.name,
				createdBy?.thirdPartyUser?.name,
			),
		],
	];

	return (
		<SearchResult
			productConfigs={productConfigs}
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={processExcerpt(excerpt ?? undefined, true)} />}
			headerMetadataParts={headerMetadataParts}
			title={enrichExcerpt(title, formatKey(key))}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};

const getLabel = (entityNoun: string) => entityNoun.replace(/-/g, ' ');

export const isSearchGraphJiraDcProject = (
	node?: any,
): node is searchGraphJiraDcProject_AtlassianSearchPage$key => {
	const { resourceType } = AnyAri.parse(node.id);
	return (
		node?.__typename === 'SearchResultGraphDocument' &&
		(resourceType === ResourceType.PROJECT || node?.subtype === ResourceType.PROJECT)
	);
};
